import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPhone(p: string | null | undefined): string {
  if (!p || p === "5555555555") return "—";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

export function formatLocation(loc: string | null | undefined): string {
  if (!loc) return "—";
  const [city, ...rest] = loc.split(", ");
  const formattedCity = city
    ? city.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
  const formattedState = rest.join(", ").toUpperCase();
  return [formattedCity, formattedState].filter(Boolean).join(", ") || "—";
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const s = iso.trim();
    if (/^\d{8}$/.test(s)) {
      // Most Fred DB dates are YYYYMMDD — try that first
      const ymdMonth = parseInt(s.slice(4, 6), 10);
      const ymdDay   = parseInt(s.slice(6, 8), 10);
      if (ymdMonth >= 1 && ymdMonth <= 12 && ymdDay >= 1 && ymdDay <= 31) {
        return `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}`;
      }
      // Fall back to MMDDYYYY for records stored in legacy format
      const mdyMonth = parseInt(s.slice(0, 2), 10);
      const mdyDay   = parseInt(s.slice(2, 4), 10);
      if (mdyMonth >= 1 && mdyMonth <= 12 && mdyDay >= 1 && mdyDay <= 31) {
        return `${s.slice(0, 2)}/${s.slice(2, 4)}/${s.slice(4)}`;
      }
    }
    return new Date(iso).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Converts a legacy YYYYMMDD Fred DB date (see formatDate above) to the
// YYYY-MM-DD format required by <input type="date">. Returns "" when `raw`
// isn't a valid YYYYMMDD string so the input just renders blank rather than
// throwing — callers should not assume a non-empty result.
export function ymdToInputDate(raw: string | null | undefined): string {
  if (!raw) return "";
  const s = raw.trim();
  if (!/^\d{8}$/.test(s)) return "";
  const month = parseInt(s.slice(4, 6), 10);
  const day = parseInt(s.slice(6, 8), 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

// Inverse of ymdToInputDate — converts an <input type="date"> value
// (YYYY-MM-DD) back to the legacy YYYYMMDD format the backend expects.
// Returns "" for an empty/malformed value.
export function inputDateToYmd(value: string): string {
  const s = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  return s.replace(/-/g, "");
}

// Renders an ISO timestamp as "just now" / "N minutes/hours/days ago", e.g.
// for the Low Inventory Alerts "notified" status pill. Falls back to an
// absolute "on Mon D" date past ~30 days, since "31 days ago" stops being a
// useful at-a-glance signal.
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return `on ${new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}
